/**
 * User Profile Page
 *
 * Purpose: Self-service profile page reached by clicking the username/avatar in the
 *          TopBanner. The signed-in person edits their own shared profile (name,
 *          job title, country, phone, time zone, locale) and sees, read-only, what
 *          they hold in each tenant: Company, roles and security attributes
 *          (ORCH-336; PRD User Manager UM-14, UM-16). Their Company in the active
 *          tenant sits on the profile card (ORCH-410 §4.2): editable only with
 *          `user.edit` there, saved to the membership; for one of Orchestrate's own
 *          people it is OrchestrateHQ, read-only.
 *
 * Architecture: SkeletonPage shell with the same ProfileCard the User Manager's user
 *               page uses (TemplateForm bound to CreateUserTemplate, so extra fields
 *               attached to the user model appear automatically), and MyAccess.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import { Home as HomeIcon, Person as PersonIcon } from '@mui/icons-material';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { NotificationSnackbar } from '../../components/common';
import CompanyField from '../../components/user/CompanyField';
import MyAccess from '../../components/user/MyAccess';
import ProfileCard, { ProfileValues } from '../../components/user/ProfileCard';
import { COMPANY_FIELD } from '../../components/user/membership';
import { useMembershipFields } from '../../components/user/useMembershipFields';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';
import userManagementService, {
  newChangeId,
  userErrorMessage,
} from '../../services/userManagementService';
import { Member } from '../../types/userManagement';

const pageConfig = {
  pageTitle: 'My Profile',
  breadcrumbs: [
    { label: 'Home', href: '/home', icon: <HomeIcon /> },
    { label: 'My Profile', href: '/profile', icon: <PersonIcon /> },
  ],
};

const UserProfilePage: React.FC = () => {
  const { state, refreshUser, can } = useAuth();
  const authUser = state.user;
  const metadataTenantId = authUser?.activeTenant?.tenant_id ?? 'GLOBAL';
  const { attributeFieldIds } = useMembershipFields(metadataTenantId);

  const [profile, setProfile] = useState<ProfileValues | null>(null);
  // The active tenant's membership: its Company shows on the profile card
  const [memberships, setMemberships] = useState<Member[]>([]);
  const [company, setCompany] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!authUser) {
        throw new Error('Not authenticated');
      }
      const me = await userManagementService.getCurrentUserProfile();
      setProfile({
        user_id: me.userId,
        email: me.email,
        first_name: me.firstName,
        last_name: me.lastName,
        job_title: me.jobTitle,
        phone: me.phone,
        country: me.country,
        time_zone: me.timeZoneOverride,
        locale_override: me.localeOverride,
        clock: me.clock,
        language: me.languageOverride,
      });
      const own = await userManagementService.getOwnMemberships();
      setMemberships(own);
      setCompany(
        own.find((m) => m.tenant_id === metadataTenantId)?.metadata[COMPANY_FIELD] ?? null
      );
    } catch (err: unknown) {
      setError(userErrorMessage(err, 'Failed to load profile'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const membership = memberships.find((m) => m.tenant_id === metadataTenantId);
  const storedCompany = membership?.metadata[COMPANY_FIELD] ?? null;
  // One of Orchestrate's own people (an active GLOBAL membership) always works for OrchestrateHQ
  const companyLocked = memberships.some((m) => m.tenant_id === 'GLOBAL');
  const canEditCompany = !!membership && can(PermissionKey.USER_EDIT) && !companyLocked;
  const companyChanged =
    canEditCompany && JSON.stringify(company ?? null) !== JSON.stringify(storedCompany);

  return (
    <SkeletonPage {...pageConfig}>
      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !authUser ? (
          <Alert severity="error">You must be signed in to view your profile.</Alert>
        ) : (
          <Stack spacing={3}>
            {profile && (
              <ProfileCard
                profile={profile}
                tenantId={metadataTenantId}
                readOnly={false}
                hint="Shared by every tenant you belong to. Your email is managed by an administrator."
                company={
                  membership && (
                    <CompanyField
                      tenantId={metadataTenantId}
                      attributeFieldIds={attributeFieldIds}
                      initialValue={storedCompany}
                      value={company}
                      onChange={setCompany}
                      permission={PermissionKey.USER_EDIT}
                      readOnly={!canEditCompany}
                      locked={companyLocked}
                    />
                  )
                }
                extraDirty={companyChanged}
                onSave={async (profileFields) => {
                  // One Save, one History entry, whether or not the Company changed too (ORCH-422)
                  const changeId = newChangeId();
                  const saved = await userManagementService.updateCurrentUserProfile(
                    { metadata: profileFields },
                    changeId
                  );
                  setProfile(saved);
                  if (companyChanged && authUser) {
                    // The Company is a membership value, saved to this tenant's membership
                    const updated = await userManagementService.updateMembership(
                      authUser.userId,
                      metadataTenantId,
                      { [COMPANY_FIELD]: company },
                      { changeId }
                    );
                    setMemberships((current) =>
                      current.map((m) => (m.tenant_id === metadataTenantId ? updated : m))
                    );
                  }
                  setSuccessMessage('Profile updated successfully');
                  // Country, Locale or Time Format may have changed what the person formats in
                  await refreshUser();
                }}
              />
            )}
            <MyAccess activeTenantId={metadataTenantId} />
          </Stack>
        )}
      </Box>

      <NotificationSnackbar
        open={!!error}
        onClose={() => setError(null)}
        message={error || ''}
        severity="error"
        autoHideDuration={6000}
      />
      <NotificationSnackbar
        open={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        message={successMessage || ''}
        severity="success"
        autoHideDuration={4000}
      />
    </SkeletonPage>
  );
};

export default UserProfilePage;
